import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tabs, Space } from "@mantine/core";

import NotFound from "../../../common/pages/NotFound";
import { NewsFeed } from "../components";
import { IntegrationsHealthTable } from "../integrations-health";
import { useGetNewsArticlesApiService } from "../services/api";
import EmployerReportFilter from "../components/EmployerReportFilter";
import EmployerReportContainer from "../components/EmployerReportContainer";
import EmployerReportProvider from "../providers/EmployerReportProvider";
import PageContainer from "../../../themes/components/PageContainer";
import PageHeading from "../../../themes/components/PageHeading";
import DesktopOnlyBanner from "../../../themes/components/DesktopOnlyBanner";
import { AccessPermissionContext } from "../../../providers/AccessPermissionsProvider";
import { AuthContext } from "../../../providers/AuthProvider";
import UserAccessControlUtils, {
  USER_ROLE_MAPPING,
  FEATURE_INTEGRATIONS,
  FEATURE_TIME_AND_ATTENDANCE,
} from "../../../utils/UserAccessControlUtils";

export const TABS = {
  CHANGELOG: {
    TAB: "changelog",
    PATH: "changelog",
  },
  REPORTING: {
    TAB: "reporting",
    PATH: "reporting",
  },
  INTEGRATIONS_HEALTH: {
    TAB: "integrations-health",
    PATH: "integrations-health",
  },
};

const TAB_INDEX: Record<string, number> = {
  [TABS.CHANGELOG.TAB]: 0,
  [TABS.REPORTING.TAB]: 1,
  [TABS.INTEGRATIONS_HEALTH.TAB]: 2,
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const auth = useContext(AuthContext);
  const displayName = auth?.first_name ?? auth?.email;
  const hasCompanyActivityAccess = auth?.user_role === USER_ROLE_MAPPING.ADMIN;
  const hasIntegrationsAccess = UserAccessControlUtils.hasFeature(
    auth?.user_features || [],
    FEATURE_INTEGRATIONS
  );
  const hasTimeAttendance = UserAccessControlUtils.hasFeature(
    auth?.user_features || [],
    FEATURE_TIME_AND_ATTENDANCE
  );
  const { hasPermissions } = React.useContext(AccessPermissionContext);

  const { data: articles } = useGetNewsArticlesApiService();

  const [activeTab, setActiveTab] = useState(() =>
    hash.length > 0 && hasCompanyActivityAccess // Allows for direct navigation to the reporting tab
      ? hash.split("#")[1]
      : TABS.CHANGELOG.PATH
  );

  useEffect(() => {
    navigate(`/home#${activeTab}`);
  }, [activeTab]);

  const onTabChange = (value: number, tabKey: string): void =>
    setActiveTab(tabKey);

  if (!hasPermissions) {
    return <NotFound />;
  }

  return (
    <PageContainer className="h-screen max-h-screen">
      <PageHeading title={`Hello, ${displayName}!`} />

      {/* Phone-only welcome + work-in-progress callout / desktop-only notice */}
      <div className="sm:hidden mt-sm">
        <p className="text-base text-greyscale-text-caption leading-24 mb-md">
          Welcome back to Jem Hub. See what&apos;s new at Jem and review your
          company&apos;s activities.
        </p>
        {hasTimeAttendance ? (
          <div className="rounded-md border border-primary-navy-200 bg-primary-navy-50 p-sm">
            <p className="text-sm font-semibold text-greyscale-text-title leading-tight m-0">
              Mobile experience - work in progress
            </p>
            <p className="text-xs text-greyscale-text-caption mt-xxxs m-0">
              Time &amp; Attendance is the only feature currently optimised for
              mobile. Other pages are best viewed on a larger screen.
            </p>
          </div>
        ) : (
          <DesktopOnlyBanner />
        )}
      </div>

      <Space h="xl" />
      <Space h="xl" />
      <Tabs
        className="hidden sm:block"
        active={TAB_INDEX[activeTab] ?? 0}
        onTabChange={onTabChange}
        tabPadding="xl"
      >
        <Tabs.Tab label="News Feed" tabKey={TABS.CHANGELOG.TAB}>
          <div
            className="overflow-y-auto"
            style={{ height: "calc(100vh - 220px)" }}
          >
            <NewsFeed articles={articles} />
          </div>
        </Tabs.Tab>
        <Tabs.Tab
          label="Company activity"
          tabKey={TABS.REPORTING.TAB}
          disabled={!hasCompanyActivityAccess}
        >
          <EmployerReportProvider>
            <div style={{ position: "relative", paddingTop: "24px" }}>
              <EmployerReportFilter />
              <EmployerReportContainer />
            </div>
          </EmployerReportProvider>
        </Tabs.Tab>
        {hasIntegrationsAccess && (
          <Tabs.Tab
            label="Integration Health"
            tabKey={TABS.INTEGRATIONS_HEALTH.TAB}
          >
            <div
              className="overflow-y-auto"
              style={{ height: "calc(100vh - 220px)" }}
            >
              <IntegrationsHealthTable />
            </div>
          </Tabs.Tab>
        )}
      </Tabs>
    </PageContainer>
  );
};

export default HomePage;
